import { useEffect, useState } from 'react';
import axios from 'axios';

// Company-wide behaviour defaults (Settings → Company Defaults). Cached in
// memory + localStorage so pages can read them synchronously on first render.
const API = `${window.__BACKEND_URL__}/api`;
const KEY = 'ht_company_defaults';

export const COMPANY_DEFAULTS_BASE = {
  jobs_default_scope: 'mine',
  jobs_show_team_badge: true,
  payroll_page_size: 50,
  payroll_remember_view: true,
  reports_default_report: 'company-kpi',
  reports_default_range: 'this_pay_period',
  invoices_default_template_id: null,
  payment_reminders_all_customers: false,
  payment_reminders_steps: [3, 14, 30],
  clockin_require_real_project: false,
};

let cache = null;
const listeners = new Set();

export const readCompanyDefaults = () => {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) cache = { ...COMPANY_DEFAULTS_BASE, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return cache || COMPANY_DEFAULTS_BASE;
};

const publish = (next) => {
  cache = { ...COMPANY_DEFAULTS_BASE, ...next };
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* ignore */ }
  listeners.forEach((fn) => fn(cache));
};

export const clearCompanyDefaultsCache = () => {
  cache = null;
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
};

export const fetchCompanyDefaults = async (token) => {
  const { data } = await axios.get(`${API}/company/defaults`, { headers: { Authorization: `Bearer ${token}` } });
  publish(data);
  return cache;
};

export const saveCompanyDefaults = async (token, patch) => {
  const { data } = await axios.put(`${API}/company/defaults`, patch, { headers: { Authorization: `Bearer ${token}` } });
  publish(data);
  return cache;
};

// Returns { defaults, loaded } — `loaded` flips true once the server copy arrived.
export function useCompanyDefaults(token) {
  const [defaults, setDefaults] = useState(readCompanyDefaults);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    listeners.add(setDefaults);
    return () => listeners.delete(setDefaults);
  }, []);
  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    fetchCompanyDefaults(token).then(() => { if (alive) setLoaded(true); }).catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [token]);
  return { defaults, loaded };
}
